import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material'
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { riderService } from '../services'
import type {
  RiderTicket,
  RiderTicketPriority,
  RiderTicketStatus,
} from '../services'

const DEFAULT_PRIORITY: RiderTicketPriority = 'MEDIUM'
const TICKET_PRIORITIES: RiderTicketPriority[] = ['LOW', 'MEDIUM', 'HIGH']
const TICKET_STATUSES: RiderTicketStatus[] = ['PENDING', 'COMPLETED', 'REJECTED']

const STATUS_LABELS: Record<RiderTicketStatus, string> = {
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
}

const PRIORITY_LABELS: Record<RiderTicketPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
}

const statusChipColor = (status: RiderTicketStatus) => {
  if (status === 'COMPLETED') return 'success'
  if (status === 'REJECTED') return 'error'
  return 'warning'
}

const RiderTickets: React.FC = () => {
  const [tickets, setTickets] = useState<RiderTicket[]>([])
  const [statusCounts, setStatusCounts] = useState<Record<RiderTicketStatus, number>>({
    PENDING: 0,
    COMPLETED: 0,
    REJECTED: 0,
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<RiderTicketStatus | ''>('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const [formState, setFormState] = useState<{
    riderId: string
    issueCategory: string
    issueDescription: string
    priority: RiderTicketPriority
  }>({
    riderId: '',
    issueCategory: '',
    issueDescription: '',
    priority: DEFAULT_PRIORITY,
  })

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [ticketDetailDialogOpen, setTicketDetailDialogOpen] = useState(false)
  const [ticketStatusDialogOpen, setTicketStatusDialogOpen] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState<RiderTicket | null>(null)
  const [statusUpdate, setStatusUpdate] = useState<Exclude<RiderTicketStatus, 'PENDING'>>('COMPLETED')
  const [issueSubject, setIssueSubject] = useState('')

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity })
  }

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const openCreateDialog = () => {
    resetForm()
    setCreateDialogOpen(true)
  }

  const closeCreateDialog = () => {
    setCreateDialogOpen(false)
    resetForm()
  }

  const loadStatusCounts = async () => {
    try {
      const response = await riderService.getRiderTicketStatusCounts()
      if (response.success && Array.isArray(response.data)) {
        const counts = { PENDING: 0, COMPLETED: 0, REJECTED: 0 }
        response.data.forEach((item) => {
          counts[item.status] = item._count?.status || 0
        })
        setStatusCounts(counts)
      }
    } catch (error) {
      console.error('Unable to load ticket status counts', error)
      showSnackbar('Failed to load ticket status counts', 'error')
    }
  }

  const loadTickets = async () => {
    setLoading(true)
    try {
      const response = await riderService.getRiderTickets({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      })

      if (response.success) {
        setTickets(response.data || [])
        setTotalCount(response.pagination?.totalItems || 0)
        setPage((response.pagination?.page || 1) - 1)
      } else {
        setTickets([])
        setTotalCount(0)
        showSnackbar(response.message || 'Failed to load tickets', 'error')
      }
    } catch (error) {
      console.error('Unable to fetch rider tickets', error)
      showSnackbar('Failed to fetch rider tickets', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatusCounts()
  }, [])

  useEffect(() => {
    loadTickets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter])

  const resetForm = () => {
    setFormState({
      riderId: '',
      issueCategory: '',
      issueDescription: '',
      priority: DEFAULT_PRIORITY,
    })
  }

  const handleCreateTicket = async () => {
    if (!formState.riderId || !formState.issueCategory || !formState.issueDescription) {
      showSnackbar('Please fill rider ID, issue category, and issue description', 'error')
      return
    }

    setSubmitting(true)
    try {
      const response = await riderService.createRiderTicket({
        riderId: formState.riderId,
        issueCategory: formState.issueCategory,
        issueDescription: formState.issueDescription,
        priority: formState.priority,
      })

      if (response.success) {
        showSnackbar('Ticket created successfully', 'success')
        closeCreateDialog()
        loadTickets()
        loadStatusCounts()
      } else {
        showSnackbar(response.message || 'Failed to create ticket', 'error')
      }
    } catch (error) {
      console.error('Error creating rider ticket', error)
      showSnackbar('Unable to create rider ticket', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const openDetailDialog = (ticket: RiderTicket) => {
    setSelectedTicket(ticket)
    setTicketDetailDialogOpen(true)
  }

  const closeDetailDialog = () => {
    setTicketDetailDialogOpen(false)
    setSelectedTicket(null)
  }

  const openStatusDialog = (ticket: RiderTicket) => {
    setSelectedTicket(ticket)
    setStatusUpdate('COMPLETED')
    setIssueSubject('')
    setTicketStatusDialogOpen(true)
  }

  const closeStatusDialog = () => {
    setTicketStatusDialogOpen(false)
    setSelectedTicket(null)
  }

  const handleUpdateTicketStatus = async () => {
    if (!selectedTicket) return
    if (!issueSubject.trim()) {
      showSnackbar('Issue subject is required to update status', 'error')
      return
    }

    setSubmitting(true)
    try {
      const response = await riderService.updateRiderTicketStatus(selectedTicket.ticketId, {
        status: statusUpdate,
        issueSubject,
      })

      if (response.success) {
        showSnackbar('Ticket status updated successfully', 'success')
        closeStatusDialog()
        loadTickets()
        loadStatusCounts()
      } else {
        showSnackbar(response.message || 'Failed to update ticket status', 'error')
      }
    } catch (error) {
      console.error('Error updating ticket status', error)
      showSnackbar('Unable to update ticket status', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box padding={3}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" marginBottom={2}>
        <Grid item>
          <Typography variant="h4" gutterBottom>
            Rider Tickets
          </Typography>
          <Typography color="textSecondary">
            Manage all rider support tickets, track status, and update priority.
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add Ticket
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} marginBottom={2}>
        {(['PENDING', 'COMPLETED', 'REJECTED'] as RiderTicketStatus[]).map((status) => (
          <Grid item xs={12} sm={4} key={status}>
            <Card elevation={1} sx={{ borderLeft: 4, borderColor: status === 'COMPLETED' ? 'success.main' : status === 'REJECTED' ? 'error.main' : 'warning.main' }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  {STATUS_LABELS[status]}
                </Typography>
                <Typography variant="h4" fontWeight="700">
                  {statusCounts[status] ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Grid container spacing={2} marginBottom={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label="Search tickets"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by rider, ticket ID, category"
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" />,
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    loadTickets()
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as RiderTicketStatus | '')
                    setPage(0)
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {TICKET_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={5} display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('')
                  loadTickets()
                  loadStatusCounts()
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  loadTickets()
                  loadStatusCounts()
                }}
              >
                Reload
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket ID</TableCell>
                  <TableCell>Public Rider ID</TableCell>
                  <TableCell>Rider Name</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : tickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No tickets found.
                    </TableCell>
                  </TableRow>
                ) : (
                  tickets.map((ticket) => (
                    <TableRow
                key={ticket.ticketId}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => openDetailDialog(ticket)}
              >
                      <TableCell>{ticket.ticketId}</TableCell>
                      <TableCell>{ticket.publicRiderId || ticket.ev91RiderId || ticket.riderId}</TableCell>
                      <TableCell>{ticket.riderName}</TableCell>
                      <TableCell>{ticket.city}</TableCell>
                      <TableCell>{ticket.mobileNumber}</TableCell>
                      <TableCell>{ticket.issueCategory}</TableCell>
                      <TableCell>{PRIORITY_LABELS[ticket.priority]}</TableCell>
                      <TableCell>
                        <Alert severity={statusChipColor(ticket.status)} sx={{ padding: '8px 12px', fontSize: '0.9rem' }}>
                          {STATUS_LABELS[ticket.status]}
                        </Alert>
                      </TableCell>
                      <TableCell>{new Date(ticket.createdAt).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          disabled={ticket.status !== 'PENDING'}
                          onClick={(event) => {
                            event.stopPropagation()
                            openStatusDialog(ticket)
                          }}
                        >
                          Update Status
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Rider Ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} marginTop={1}>
            <TextField
              label="Rider ID"
              value={formState.riderId}
              onChange={(e) => setFormState((prev) => ({ ...prev, riderId: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Issue Category"
              value={formState.issueCategory}
              onChange={(e) => setFormState((prev) => ({ ...prev, issueCategory: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Issue Description"
              value={formState.issueDescription}
              onChange={(e) => setFormState((prev) => ({ ...prev, issueDescription: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={formState.priority}
                onChange={(e) => setFormState((prev) => ({ ...prev, priority: e.target.value as RiderTicketPriority }))}
              >
                {TICKET_PRIORITIES.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {PRIORITY_LABELS[priority]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTicket} disabled={submitting} startIcon={<AddIcon />}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ticketDetailDialogOpen} onClose={closeDetailDialog} fullWidth maxWidth="md">
        <DialogTitle>Ticket Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} marginTop={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Ticket ID
              </Typography>
              <Typography>{selectedTicket?.ticketId ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Public Rider ID
              </Typography>
              <Typography>{selectedTicket?.publicRiderId || selectedTicket?.ev91RiderId || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Rider Name
              </Typography>
              <Typography>{selectedTicket?.riderName ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                City
              </Typography>
              <Typography>{selectedTicket?.city ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Mobile
              </Typography>
              <Typography>{selectedTicket?.mobileNumber ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Issue Category
              </Typography>
              <Typography>{selectedTicket?.issueCategory ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Issue Description
              </Typography>
              <Typography>{selectedTicket?.issueDescription ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Issue Subject
              </Typography>
              <Typography>{selectedTicket?.issueSubject ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Priority
              </Typography>
              <Typography>{selectedTicket ? PRIORITY_LABELS[selectedTicket.priority] : '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Status
              </Typography>
              <Typography>{selectedTicket ? STATUS_LABELS[selectedTicket.status] : '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Created At
              </Typography>
              <Typography>{selectedTicket ? new Date(selectedTicket.createdAt).toLocaleString() : '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Updated At
              </Typography>
              <Typography>{selectedTicket ? new Date(selectedTicket.updatedAt).toLocaleString() : '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Resolved At
              </Typography>
              <Typography>{selectedTicket?.resolvedAt ? new Date(selectedTicket.resolvedAt).toLocaleString() : '-'}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ticketStatusDialogOpen} onClose={closeStatusDialog} fullWidth maxWidth="sm">
        <DialogTitle>Update Ticket Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} marginTop={1}>
            <Typography>
              Ticket: {selectedTicket?.ticketId} | Rider: {selectedTicket?.riderName}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusUpdate}
                onChange={(event) => setStatusUpdate(event.target.value as Exclude<RiderTicketStatus, 'PENDING'>)}
              >
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Issue Subject"
              value={issueSubject}
              onChange={(e) => setIssueSubject(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateTicketStatus}
            disabled={submitting}
            startIcon={<CheckCircleIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default RiderTickets
